import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ThemeProviderContext } from '@knack/asterisk-react';

import { cn } from '@/utils/tailwind';
import { BASE_IMAGE_URL } from '@/pages/apps/create-app/constants';
import { translationKeyPrefixHorizontalUI } from '@/pages/apps/create-app/setup-wizard-variations/constants';
import { TEMPLATE_CARD_CLASSES } from '@/pages/apps/create-app/setup-wizard-variations/helper';

export interface TemplateCardHorizontalUIProps {
  type: 'template' | 'import' | 'ai';
  onClick: () => void;
}

function getImageUrl(type: 'template' | 'import' | 'ai', isDarkMode: boolean): string {
  switch (type) {
    case 'template':
      return `${BASE_IMAGE_URL}${isDarkMode ? 'dark' : 'light'}-template.svg`;
    case 'import':
      return `${BASE_IMAGE_URL}${isDarkMode ? 'dark' : 'light'}-start-table.svg`;
    case 'ai':
      return `${BASE_IMAGE_URL}${isDarkMode ? 'dark' : 'light'}-other.svg`;
    default:
      return '';
  }
}

function TemplateCard({
  title,
  description,
  imageUrl
}: {
  title: string;
  description: string;
  imageUrl: string;
}) {
  return (
    <>
      <img src={imageUrl} alt={title} className="size-[100px] rounded-lg object-cover" />
      <div className="flex flex-col justify-start p-4">
        <h3 data-testid="card-title" className="line-clamp-1 text-md font-semibold text-emphasis">
          {title}
        </h3>
        <p data-testid="card-description" className="mt-1 w-full text-xs text-subtle sm:text-sm">
          {description}
        </p>
      </div>
    </>
  );
}

export function TemplateCardHorizontalUI(props: TemplateCardHorizontalUIProps) {
  const { type, onClick } = props;

  const { isDarkMode } = useContext(ThemeProviderContext);
  const { t } = useTranslation();
  const BASE_CARD_PREFIX = `${translationKeyPrefixHorizontalUI}.step_2.cards`;

  const title = t(`${BASE_CARD_PREFIX}.${type}.title`);
  const description = t(`${BASE_CARD_PREFIX}.${type}.description`);
  const imageUrl = getImageUrl(type, isDarkMode);

  return (
    <Card
      data-testid={`${type}-card`}
      className={cn(TEMPLATE_CARD_CLASSES, 'p-3 sm:p-2')}
      onClick={onClick}
    >
      <TemplateCard title={title} description={description} imageUrl={imageUrl} />
    </Card>
  );
}
